import { makeStyles } from '@material-ui/core/styles/index';

const useStyles = makeStyles(theme => ({
  container: {
    maxWidth: '800px',
    margin: '1rem auto',
    padding: '2rem',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  header: {
    marginBottom: '2rem',
    '& h2': {
      fontSize: '1.5rem',
      fontWeight: 600,
      color: theme.palette.text.primary,
      marginBottom: '1rem',
    },
    '& p': {
      color: theme.palette.text.secondary,
      fontSize: '1rem',
      lineHeight: 1.5,
    },
  },
  infoSection: {
    backgroundColor: theme.palette.grey[50],
    padding: '1.5rem',
    borderRadius: '6px',
    marginTop: '2rem',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 0',
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  infoItemLabel: {
    width: '120px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  infoItemValue: {
    flex: 1,
    color: theme.palette.text.primary,
    fontFamily: 'monospace',
  },
  form: {
    width: '100%',
    maxWidth: '600px',
    margin: '0 auto',
  },
  information: {
    textAlign: 'center',
    marginBottom: '2rem',
    padding: '1rem',
    backgroundColor: theme.palette.primary[50],
    borderRadius: '6px',
    '& p': {
      margin: '0.5rem 0',
      color: theme.palette.text.secondary,
    },
  },
  domainList: {
    color: theme.palette.primary.main,
    fontWeight: 500,
    margin: '0.5rem 0',
  },
  buttonGroup: {
    display: 'flex',
    gap: '16px',
    marginTop: '24px',
    justifyContent: 'flex-end',
  },
  buttonContainer: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  progressContainer: {
    marginTop: '1rem',
  },
  validationResult: {
    marginTop: '2rem',
    borderRadius: '6px',
    backgroundColor: theme.palette.grey[50],
  },
  successAssignment: {
    textAlign: 'center',
    marginTop: '50px',
    padding: '2rem',
    '& h3': {
      color: theme.palette.success.main,
      marginBottom: '1rem',
    },
  },
  green: {
    color: theme.palette.success.main,
  },
  red: {
    color: theme.palette.error.main,
  },
  bold: {
    fontWeight: 600,
  },
  root: {
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    transition: 'all 0.2s ease-in-out',
  },
  label: {
    minWidth: '140px',
    fontWeight: 500,
    color: theme.palette.text.secondary,
  },
  value: {
    flex: 1,
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    wordBreak: 'break-all',
    padding: '0.25rem 0',
  },
  copyButton: {
    flexShrink: 0,
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  copyIcon: {
    width: 16,
    height: 16,
    color: theme.palette.primary.main,
  },
  successIcon: {
    color: theme.palette.success.main,
    fontSize: '1.25rem',
  },
  tooltip: {
    fontSize: '0.75rem',
  },
  customTenantDomainContainer: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
    '& svg': {
      fontSize: 20,
    },
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.error.main,
    marginBottom: theme.spacing(2),
    '& svg': {
      fontSize: 20,
    },
  },
  customDomainName: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.grey[200]}`,
    '& .MuiTypography-root': {
      fontSize: '1.1rem',
      fontWeight: 500,
      color: theme.palette.text.primary,
    },
  },
  deleteButton: {
    '&:hover': {
      backgroundColor: theme.palette.error.light,
      '& img': {
        filter: 'brightness(0) invert(1)',
      },
    },
    transition: 'all 0.2s ease-in-out',
  },
  checkIcon: {
    color: theme.palette.success.main,
    fontSize: 20,
    marginRight: theme.spacing(1),
  },
  errorIcon: {
    color: theme.palette.error.main,
    fontSize: 20,
    marginRight: theme.spacing(1),
  },
  stepContainer: {
    marginBottom: '24px',
  },
  stepTitle: {
    fontWeight: 'bold',
  },
  formContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
  },
  alert: {
    marginBottom: 24,
    fontSize: 16,
    backgroundColor: '#c5e3fc',
    color: 'black',
  },
  customTenantDomainAlert: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  domainLink: {
    color: 'blue',
    textDecoration: 'none',
    fontSize: '1.1rem',
    fontWeight: 500,
    '&:hover': {
      textDecoration: 'underline',
      color: 'blue',
    },
  },
  uploadContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
  loadingBar: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  bindingPrompt: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  bindingText: {
    marginBottom: theme.spacing(2),
  },
  countdown: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
    color: theme.palette.primary.main,
  },
  bindButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    '& svg': {
      fontSize: 20,
    },
  },
  table: {
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
    overflow: 'hidden',
    [theme.breakpoints.down('sm')]: {
      display: 'block',
      overflowX: 'auto',
    },
  },
  tableHeader: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    padding: theme.spacing(2),
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
    },
  },
  tableCell: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'nowrap',
    },
  },
  typeCell: {
    width: '15%',
    minWidth: '100px',
  },
  nameCell: {
    width: '25%',
    minWidth: '150px',
  },
  contentCell: {
    width: '60%',
    minWidth: '350px',
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  contentWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    maxWidth: '100%',
  },
  contentText: {
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
  },
  tableRow: {
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td': {
      borderBottom: 0,
    },
  },
}));

export default useStyles;
